import { z } from 'zod';
import { ChecklistItemStatus, ChecklistItemStatusSchema } from './engagement.js';

// ─── CLOSING-phase checklist template (#2245, epic #2240) ───────────────────

/**
 * @aiPurpose one row of the LOCKED CLOSING-phase checklist template (#2245
 *   FR-1/BR-2). `templateKey` is the stable, release-versioned join key the M6
 *   UI localizes; `isOptional` marks the two items (spec, contract) a manager
 *   may toggle off (FR-2).
 * @aiInvariant the template is seed data — a code constant, NOT admin-editable
 *   (DEC-11). Order + keys + `isOptional` are LOCKED; changing the set is a
 *   release change (a new constant revision), never a data mutation.
 */
export interface ClosingChecklistTemplateItem {
  readonly templateKey: string;
  readonly isOptional: boolean;
}

/**
 * @aiPurpose the fixed 7-item CLOSING checklist, in order (#2245 FR-1). On
 *   entering CLOSING an `Engagement` is seeded exactly these rows
 *   (`engagement-write-helpers.ts#seedClosingChecklist`). Two items are optional
 *   (spec, contract); `confirm_payment` (item 7) is the single conversion
 *   trigger point (FR-3/BR-4).
 * @aiInvariant single source of truth for the template — the backend seed AND
 *   the read-side ordering both derive from this array.
 */
export const CLOSING_CHECKLIST_TEMPLATE: readonly ClosingChecklistTemplateItem[] = [
  { templateKey: 'contract_legal_entities_check', isOptional: false },
  { templateKey: 'spec', isOptional: true },
  { templateKey: 'contract', isOptional: true },
  { templateKey: 'issue_invoice', isOptional: false },
  { templateKey: 'send_invoice', isOptional: false },
  { templateKey: 'receive_payment_slip', isOptional: false },
  { templateKey: 'confirm_payment', isOptional: false },
] as const;

/**
 * `templateKey` of item 7. Marking it DONE emits the conversion trigger M5
 * (#2246) turns into the atomic CLOSING→DEAL transition (INV-4). M4 fires the
 * seam only — it performs no phase change.
 */
export const CLOSING_CONVERSION_TRIGGER_KEY = 'confirm_payment';

/** Template position (0-based) of each `templateKey`, for a stable read order. */
const TEMPLATE_ORDER: ReadonlyMap<string, number> = new Map(
  CLOSING_CHECKLIST_TEMPLATE.map((item, index) => [item.templateKey, index]),
);

/**
 * Fixed template position of a `templateKey`, or `Number.MAX_SAFE_INTEGER` for
 * an unknown key (sorts it last). The DB carries no order column — the template
 * is the ordering authority.
 */
export const closingChecklistOrder = (templateKey: string): number =>
  TEMPLATE_ORDER.get(templateKey) ?? Number.MAX_SAFE_INTEGER;

// ─── Wire contract (#2245 AC-6) ─────────────────────────────────────────────

/**
 * @aiPurpose one CLOSING checklist item on the wire (#2245 AC-6). `active`
 *   (BR-3) is `false` only for an optional item a manager toggled off — it is
 *   then excluded from progress and never blocks item 7. Non-optional items are
 *   always `active`.
 */
export const ChecklistItemViewSchema = z
  .object({
    id: z.string().uuid(),
    templateKey: z.string(),
    status: ChecklistItemStatusSchema,
    isOptional: z.boolean(),
    active: z.boolean(),
  })
  .strict();
export type ChecklistItemView = z.infer<typeof ChecklistItemViewSchema>;

/**
 * @aiPurpose server-computed progress projection (#2245 AC-6). `doneActive` /
 *   `totalActive` count ACTIVE items only (an optional item toggled off drops
 *   out of both). The M6 UI renders "N/M" straight from this — it MUST NOT
 *   recompute progress from the raw item rows.
 */
export const ChecklistProgressSchema = z
  .object({
    doneActive: z.number().int().nonnegative(),
    totalActive: z.number().int().nonnegative(),
  })
  .strict();
export type ChecklistProgress = z.infer<typeof ChecklistProgressSchema>;

/** Read shape of `GET /api/engagements/:id/checklist` (#2245 AC-6). */
export const ChecklistViewSchema = z
  .object({
    items: z.array(ChecklistItemViewSchema),
    progress: ChecklistProgressSchema,
  })
  .strict();
export type ChecklistView = z.infer<typeof ChecklistViewSchema>;

/**
 * Client-settable checklist statuses (#2245 FR-2/FR-3). `SKIPPED` (M0 enum) is
 * reserved and NOT a client operation — an optional item is excluded from
 * progress via `active`, never by moving it to `SKIPPED`.
 */
export const CHECKLIST_ITEM_SETTABLE_STATUSES = [
  ChecklistItemStatus.TODO,
  ChecklistItemStatus.DONE,
] as const;

/**
 * @aiPurpose input for `PATCH /api/engagements/:id/checklist/:itemId` (#2245
 *   FR-2/FR-3). `status` marks an item TODO/DONE (DONE on item 7 emits the
 *   conversion trigger); `active` toggles an OPTIONAL item on/off. At least one
 *   field is required; `.strict()` blocks unknown keys.
 * @aiInvariant only optional items accept `active=false` (enforced by the
 *   service, DOMAIN_CONFLICT otherwise); `SKIPPED` is not settable here.
 */
export const PatchChecklistItemInputSchema = z
  .object({
    status: z.enum(CHECKLIST_ITEM_SETTABLE_STATUSES).optional(),
    active: z.boolean().optional(),
  })
  .strict()
  .refine((value) => value.status !== undefined || value.active !== undefined, {
    message: 'At least one of `status` or `active` must be provided',
  });
export type PatchChecklistItemInput = z.infer<typeof PatchChecklistItemInputSchema>;
